import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import HistoryFilters from './components/HistoryFilters';
import HistoryStats from './components/HistoryStats';
import HistoryTable from './components/HistoryTable';
import EmptyState from './components/EmptyState';
import { uploadHistoryManager } from '../../utils/uploadStorage';
import { useNotification } from '../../components/ui/NotificationProvider';

const UploadHistory = () => {
  const { showInfo, showError, showWarning, showConfirmation } = useNotification();
  const [historyData, setHistoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
    searchTerm: '',
    fileType: ''
  });
  const [isLoading, setIsLoading] = useState(true);

useEffect(() => {
  const fetchHistoryData = async () => {
    setIsLoading(true);
    try {
      const rows = uploadHistoryManager?.getHistory() || [];

      const sortedHistory = (rows || []).sort((a, b) =>
        new Date(b?.date || b?.addedAt || 0) - new Date(a?.date || a?.addedAt || 0)
      );
      setHistoryData(sortedHistory);
      setFilteredData(sortedHistory);
    } catch (error) {
      console.error('Error fetching history data:', error);
      // Último recurso: localStorage limpio
      const local = uploadHistoryManager?.getHistory() || [];
      const sorted = (local || []).sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));
      setHistoryData(sorted);
      setFilteredData(sorted);
    } finally {
      setIsLoading(false);
    }
  };

  fetchHistoryData();
}, []);

  const applyFilters = (data, currentFilters) => {
    let filtered = [...data];

    if (currentFilters?.searchTerm) {
      filtered = filtered?.filter(batch =>
        batch?.fileName?.toLowerCase()?.includes(currentFilters?.searchTerm?.toLowerCase()) ||
        batch?.uploadedBy?.toLowerCase()?.includes(currentFilters?.searchTerm?.toLowerCase())
      );
    }

    if (currentFilters?.status) {
      filtered = filtered?.filter(batch => batch?.status === currentFilters?.status);
    }

    if (currentFilters?.fileType) {
      filtered = filtered?.filter(batch => {
        const extension = batch?.fileName?.split('.')?.pop()?.toLowerCase();
        return extension === currentFilters?.fileType;
      });
    }

    if (currentFilters?.dateFrom) {
      filtered = filtered?.filter(batch => 
        new Date(batch.date) >= new Date(currentFilters.dateFrom)
      );
    }

    if (currentFilters?.dateTo) {
      filtered = filtered?.filter(batch => 
        new Date(batch.date) <= new Date(currentFilters.dateTo + 'T23:59:59')
      );
    }

    setFilteredData(filtered);
  };

  useEffect(() => {
    // Apply filters when they change
    applyFilters(historyData, filters);
  }, [filters, historyData]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      status: '',
      searchTerm: '',
      fileType: ''
    });
  };

  const handleExport = () => {
    const headers = ['Fecha', 'Archivo', 'Total Productos', 'Exitosos', 'Fallidos', 'Estado', 'Duración', 'Usuario'];
    const csvContent = [
      headers?.join(','),
      ...filteredData?.map(batch => [
        new Date(batch.date)?.toLocaleDateString('es-ES'),
        `"${batch?.fileName}"`,
        batch?.totalProducts,
        batch?.successCount,
        batch?.failedCount,
        batch?.status,
        `"${batch?.duration}"`,
        batch?.uploadedBy
      ]?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historial_cargas_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const handleRetry = (batchId) => {
    console.log('Retrying batch:', batchId);
    // Implement retry logic here - would trigger bulk upload with same data
    showInfo?.(`Reintentando lote ${batchId}. Esta funcionalidad se implementará próximamente.`, {
      title: 'Reintento',
      duration: 4000
    });
  };

  const handleDelete = (batchId) => {
    showConfirmation?.({
      title: 'Eliminar del historial',
      message: '¿Estás seguro de que deseas eliminar este lote del historial? ',
      type: 'warning',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      onConfirm: () => {
        const removed = uploadHistoryManager?.removeFromHistory(batchId);
        if (removed) {
          const updatedData = historyData?.filter(batch => batch?.id !== batchId);
          setHistoryData(updatedData);
          applyFilters(updatedData, filters);
          console.log('Deleted batch:', batchId, 'from storage and local state');
        } else {
          console.error('Failed to delete batch from storage:', batchId);
          showError?.('Error al eliminar el lote del historial. Inténtalo de nuevo.', { title: 'Error' });
        }
      }
    });
  };

  const handleViewDetails = (batch) => {
    console.log('Viewing details for batch:', batch);
    // This could open a modal with detailed information
  };

  const handleRefresh = () => {
    // Force refresh the history data
    const newHistory = uploadHistoryManager?.getHistory() || [];
    const sortedHistory = newHistory?.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    setHistoryData(sortedHistory);
    applyFilters(sortedHistory, filters);
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Historial de Cargas - MercadoLibre Bulk Publisher</title>
        </Helmet>
        
        <div className="min-h-screen bg-background">
          <main className="pt-10 px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-text-secondary">Cargando historial de cargas...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Historial de Cargas - MercadoLibre Bulk Publisher</title>
        <meta name="description" content="Revisa el historial completo de tus cargas de productos a MercadoLibre con estadísticas detalladas y opciones de reintento." />
        <meta name="keywords" content="historial cargas, MercadoLibre, productos, estadísticas, reintento" />
      </Helmet>
      <div className="min-h-screen bg-background">
        
        <main className="pt-10 px-6 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">
                  Historial de Cargas
                </h1>
                <p className="text-text-secondary">
                  Revisa y gestiona todas tus cargas de productos a MercadoLibre
                </p>
              </div>
              
              <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Actualizar
                </Button>
                <Button
                  variant="primary"
                  onClick={() => window.location.href = '/bulk-upload'}
                  iconName="Upload"
                  iconPosition="left"
                >
                  Nueva Carga
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    showConfirmation?.({
                      title: 'Eliminar historial',
                      message: '¿Deseas eliminar todo el historial de cargas? Esta acción no se puede deshacer.',
                      type: 'danger',
                      confirmText: 'Eliminar Todo',
                      cancelText: 'Cancelar',
                      onConfirm: () => {
                        const ok = uploadHistoryManager?.clearHistory();
                        if (ok) {
                          setHistoryData([]);
                          setFilteredData([]);
                          showInfo?.('Historial eliminado correctamente', { title: 'Historial Limpio' });
                        } else {
                          showError?.('No se pudo eliminar el historial.');
                        }
                      }
                    });
                  }}
                  iconName="Trash2"
                  iconPosition="left"
                >
                  Eliminar Todo
                </Button>
              </div>
            </div>

            {historyData?.length === 0 ? (
              <EmptyState 
                hasFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              />
            ) : (
              <>
                {/* Statistics */}
                <HistoryStats data={filteredData} />

                {/* Filters */}
                <HistoryFilters
                  onFiltersChange={handleFiltersChange}
                  onExport={handleExport}
                  onClearFilters={handleClearFilters}
                />

                {/* Results */}
                {filteredData?.length === 0 ? (
                  <EmptyState 
                    hasFilters={hasActiveFilters}
                    onClearFilters={handleClearFilters}
                  />
                ) : (
                  <>
                    {/* Results Summary */}
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-text-secondary">
                        Mostrando {filteredData?.length} de {historyData?.length} lotes
                        {hasActiveFilters && ' (filtrado)'}
                      </p>
                      <div className="flex items-center space-x-2 text-sm text-text-secondary">
                        <Icon name="Clock" size={16} />
                        <span>Última actualización: {new Date()?.toLocaleTimeString('es-ES')}</span>
                      </div>
                    </div>

                    {/* History Table */}
                    <HistoryTable
                      data={filteredData}
                      onRetry={handleRetry}
                      onDelete={handleDelete}
                      onViewDetails={handleViewDetails}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default UploadHistory;